import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Dispatch, Store, bindActionCreators } from "redux";
import { connect, Provider} from "react-redux";
import { objectOverride, Action, isType, actionCreatorFactory, StoreProvider } from "diy/redux/helper";
const actionCreator = actionCreatorFactory('diy/module/sectionContainer/');

import GroupOverlays, {
    StateProps as GroupOverlaysStateProps,
    DispatchProps as GroupOverlaysDispatchProps
} from "../section/components/GroupOverlays";
import {
    updateData, updateBackgroundImage,
    serializeBackgroundSettings, unserializeBackgroundSettings,
    BackgroundSettings,
    clickOutside,
    saveTransformedBackgroundImage
} from '../section/index';
import {toggleLoadingMask} from '../section/loadingMask';
import {deleteGroupAction, supportsAddGroup, openAviary, closeAviary, syncGroupsBackgroundTo} from '../section/index';
import {SectionContainerData} from '../ModulSectionContainer';
import {AppState, MODULE_SECTION_CONTAINER_STATE_KEY} from 'diy/redux/app/state';

export interface ModuleSectionContainerState {
    backgroundSettings: BackgroundSettingsState;
    backgroundMenuOpen: {
        [groupId: number]: boolean
    };
    configurableGroups: ConfigurableGroups;
    rootSectionId: number;
}
interface BackgroundSettingsState {
    [groupId: string]: BackgroundSettings;
}

export interface ConfigurableGroup {
    color: boolean;
    theming: boolean;
    image: boolean;
    video: boolean;
}

export interface ConfigurableGroups {
    [groupId: string]: ConfigurableGroup;
}

interface LoadDataPayload {
    moduleId: number;
    data: SectionContainerData;
}
export const loadData = actionCreator<LoadDataPayload>(
    'MODULE_SECTION_CONTAINER_LOAD_STATE'
);

interface ToggleBackgroundMenuPayload {
    moduleId: number;
    visible: boolean;
}
export const toggleBackgroundMenu = actionCreator<ToggleBackgroundMenuPayload>(
    "MODULE_SECTION_CONTAINER_TOGGLE_BACKGROUND_MENU"
);

function openBackgroundMenu(groupId: number) {
    return (dispatch: Dispatch, getState: () => AppState) => {
        dispatch(toggleBackgroundMenu({
            moduleId: groupId,
            visible: true
        }));
    };
}

function closeBackgroundMenu(groupId: number) {
    return (dispatch: Dispatch, getState: () => AppState) => {
        dispatch(toggleBackgroundMenu({
            moduleId: groupId,
            visible: false
        }));
    };
}

function openAviaryGroup(moduleId: number) {
    return (dispatch: Dispatch, getState: () => AppState) => {
        let ownState = getState().moduleSectionContainer;
        dispatch(openAviary({
            moduleId: moduleId,
            transformedImageUrl: ownState.backgroundSettings[moduleId].transformedImageUrl,
            origImageUrl: ownState.backgroundSettings[moduleId].origImageUrl,
            imageMediaType: ownState.backgroundSettings[moduleId].imageMediaType
        }));
    };
}

function updatePayload(moduleId: number, payload: SectionContainerData) {
    return (dispatch: Dispatch, getState: () => AppState) => {
        dispatch(toggleLoadingMask({ active: true }));
        updateData<SectionContainerData>(moduleId, payload).then((data) => {
            dispatch(loadData({moduleId, data: data}));
            dispatch(toggleLoadingMask({ active: false }));
        });
    };
}

export function changeBackgroundImage(
    moduleId: number,
    imageId: number,
    imageType: string,
    successCallback: () => void,
    errorCallback: (error?: string) => void
) {
    return (dispatch: Dispatch, getState: () => AppState) => {
        dispatch(toggleLoadingMask({ active: true }));
        updateBackgroundImage<SectionContainerData>(
            moduleId, imageId, imageType, successCallback, errorCallback
        ).then((data) => {
            dispatch(loadData({moduleId, data: data}));
            dispatch(toggleLoadingMask({ active: false }));
        });
    };
}

export function saveBackgroundImage(imageDataUrl: string) {
    return (dispatch: Dispatch, getState: () => AppState) => {
        let state = getState().moduleSection;
        dispatch(closeAviary({}));
        dispatch(toggleLoadingMask({ active: true }));
        saveTransformedBackgroundImage<SectionContainerData>(
            state.aviaryOptions.moduleId,
            imageDataUrl
        ).then(data => {
            dispatch(loadData({ moduleId: state.aviaryOptions.moduleId, data}));
            dispatch(toggleLoadingMask({ active: false }));
        });
    };
}

export function changeBackgroundSettings(moduleId: number, background: BackgroundSettings) {
    let newBackground = serializeBackgroundSettings(background);

    return updatePayload(moduleId, {
        background: newBackground
    } as SectionContainerData);
}

const mapStateToProps = (appState: AppState): GroupOverlaysStateProps => ({
    visibleOverlays: appState.moduleSection.manageGroupsActive ? appState.moduleSection.configurableGroups : [],
    backgroundSettings: appState.moduleSectionContainer.backgroundSettings,
    backgroundMenuOpen: appState.moduleSectionContainer.backgroundMenuOpen,
    configurableGroups: appState.moduleSectionContainer.configurableGroups,
    rootSectionId: appState.moduleSection.moduleId,
    showDeleteButton: appState.moduleSection.quantity && supportsAddGroup(appState.moduleSection) ?
        appState.moduleSection.quantity.current > appState.moduleSection.quantity.min :
        false
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators<GroupOverlaysDispatchProps>({
    onChangeBackgroundImage: changeBackgroundImage,
    onChangeBackground: changeBackgroundSettings,
    onEditImageClicked: openAviaryGroup,
    onSelectImageClicked: closeBackgroundMenu,
    onDeleteGroup: deleteGroupAction,
    onClickOutside: clickOutside,
    onBackgroundMenuOpened: openBackgroundMenu,
    onBackgroundMenuClosed: closeBackgroundMenu,
    onSyncGroupsBackground: syncGroupsBackgroundTo
}, dispatch);

function renderGroupOverlays(store: Store) {
    let overlaysContainer = document.createElement('div');
    overlaysContainer.setAttribute('id', 'diy-group-overlays');
    document.body.appendChild(overlaysContainer);

    const ConnectedGroupOverlays = connect(mapStateToProps, mapDispatchToProps)(GroupOverlays);

    ReactDOM.render(
        <Provider store={store}>
            <ConnectedGroupOverlays/>
        </Provider>,
        overlaysContainer
    );
}

const initialState: ModuleSectionContainerState = {
} as ModuleSectionContainerState;

// =======
// REDUCER
// =======
function reducer(
    state: ModuleSectionContainerState = initialState,
    action: Action<any>
): ModuleSectionContainerState {
    if (isType(action, loadData)) {
        let bgPayload = action.payload.data.background;
        let newBgSettings = unserializeBackgroundSettings(bgPayload);
        let configurable = action.payload.data.configurable;
        return objectOverride(state, {
            backgroundSettings: objectOverride(state.backgroundSettings, {
                [action.payload.moduleId]: newBgSettings
            }),
            configurableGroups: objectOverride(state.configurableGroups, {
                [action.payload.moduleId]: configurable
            })
        });
    }

    if (isType(action, toggleBackgroundMenu)) {
        return objectOverride(state, {
            backgroundMenuOpen: objectOverride(state.backgroundMenuOpen, {
                [action.payload.moduleId]: action.payload.visible
            })
        });
    }

    if (isType(action, openAviary)) {
        let backgroundMenuOpen: typeof state.backgroundMenuOpen = Object.assign({}, state.backgroundMenuOpen);
        for (let groupId in backgroundMenuOpen) {
            if (backgroundMenuOpen.hasOwnProperty(groupId)) {
                backgroundMenuOpen[groupId] = false;
            }
        }
        return objectOverride(state, {
            backgroundMenuOpen: backgroundMenuOpen
        });
    }

    return state;
}

// ==============
// INITIALIZATION
// ==============
let initialized = false;
export function init(storeProvider: StoreProvider) {
    if (!initialized) {
        storeProvider.addReducer(reducer, MODULE_SECTION_CONTAINER_STATE_KEY);
        initialized = true;
    }
}

export function render(store: Store) {
    renderGroupOverlays(store);
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/module/sectionContainer/index.tsx
 **/